import { useId, useMemo, useState } from "react";
import { Mail, Pencil, Phone } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";

/** Telefon i e-mail jednej osoby — klikalne, z „brak …" zamiast pustego wiersza. */
function ContactLines({ phone, email }: { phone: string | null; email: string | null }) {
  return (
    <div className="mt-1.5 flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
      <span className="flex items-center gap-1.5">
        <Phone size={12} className="shrink-0" />
        {phone ? (
          <a href={`tel:${phone}`} className="hover:underline break-all">
            {phone}
          </a>
        ) : (
          <span className="text-[var(--color-text-muted)]">brak numeru</span>
        )}
      </span>
      <span className="flex items-center gap-1.5">
        <Mail size={12} className="shrink-0" />
        {email ? (
          <a href={`mailto:${email}`} className="hover:underline break-all">
            {email}
          </a>
        ) : (
          <span className="text-[var(--color-text-muted)]">brak adresu</span>
        )}
      </span>
    </div>
  );
}

/** Zamawiający (osoba odpowiedzialna GK) jednego zamówienia urzędowego (MZ / wypisy /
 *  MPZP) — ta sama karta co przy zamówieniach podwykonawców w /orders: nazwisko plus
 *  kontakt NA ŻYWO z profilu tej osoby, więc przepięcie zamawiającego zmienia też
 *  telefon i e-mail. Przepięcie przenosi zamówienie na listę „moich" nowej osoby
 *  i daje jej uprawnienia twórcy; backend odkłada wpis „kto → kto" w historii.
 *  Gdy zamawiający odszedł (konto nieaktywne), jego uprawnienia przechodzą na osobę do
 *  kontaktów: karta pokazuje ją w podsekcji „Przejęte przez" z JEJ kontaktem, a kontakt
 *  odchodzącego — już nieaktualny — tylko w dymku po najechaniu na jego nazwisko. */
export function OrderOrdererControl({
  ordererId,
  ordererName,
  ordererPhone,
  ordererEmail,
  takenOverByName,
  takenOverByPhone = null,
  takenOverByEmail = null,
  canManage,
  pending,
  onChange,
}: {
  ordererId: string | null;
  ordererName: string | null;
  ordererPhone: string | null;
  ordererEmail: string | null;
  takenOverByName?: string;
  takenOverByPhone?: string | null;
  takenOverByEmail?: string | null;
  canManage: boolean;
  pending: boolean;
  onChange: (userId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState("");
  const authorTipId = useId();
  // Lista ładuje się dopiero po wejściu w edycję — na stronie zamówienia i tak
  // zwykle już jest w cache (picker osoby do kontaktu używa tej samej listy).
  const { data: internalUsers } = useInternalUsers({ enabled: editing });
  const options = useMemo(
    () =>
      (internalUsers ?? []).map((u) => ({
        value: String(u.id),
        label: u.full_name || u.username,
      })),
    [internalUsers],
  );

  return (
    <div className="card border-l-4 border-l-[var(--color-primary)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] text-[var(--color-text-muted)] mb-0.5">
            Zamawiający (osoba odpowiedzialna)
          </p>
          {takenOverByName ? (
            <>
              {/* Dymek jest potomkiem grupy, a odstęp robi `pt-1` (nie margines), więc
                  przejście myszą na telefon/e-mail w dymku go nie zamyka. */}
              <span
                className="group relative inline-block"
                tabIndex={0}
                aria-describedby={authorTipId}
              >
                <span className="text-sm font-medium break-words cursor-help underline decoration-dotted underline-offset-2">
                  {ordererName || "—"}
                </span>
                <span className="text-[11px] text-[var(--color-text-muted)]"> · konto nieaktywne</span>
                <span
                  id={authorTipId}
                  role="tooltip"
                  className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity absolute left-0 top-full z-20 pt-1"
                >
                  <span className="block w-max max-w-[18rem] border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg p-2">
                    <span className="block text-[11px] text-[var(--color-text-muted)]">
                      Kontakt autora (konto nieaktywne)
                    </span>
                    <ContactLines phone={ordererPhone} email={ordererEmail} />
                  </span>
                </span>
              </span>
              <div className="mt-2 border-l-2 border-l-[var(--color-primary)] pl-2.5">
                <p className="text-[11px] text-[var(--color-text-muted)] mb-0.5">Przejęte przez</p>
                <p className="text-sm font-medium break-words">{takenOverByName}</p>
                <ContactLines phone={takenOverByPhone} email={takenOverByEmail} />
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-medium break-words">{ordererName || "—"}</p>
              <ContactLines phone={ordererPhone} email={ordererEmail} />
            </>
          )}
        </div>
        {canManage && !editing && (
          <Button
            size="sm"
            variant="ghost"
            loading={pending}
            onClick={() => {
              setSelected(ordererId ?? "");
              setEditing(true);
            }}
          >
            <Pencil size={12} className="mr-1" /> Zmień
          </Button>
        )}
      </div>
      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="w-60">
            <SearchableSelect
              options={options}
              value={selected}
              placeholder="Wybierz osobę…"
              onChange={(e) => setSelected(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            loading={pending}
            disabled={!selected || selected === (ordererId ?? "")}
            onClick={() => {
              onChange(selected);
              setEditing(false);
            }}
          >
            Zapisz
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Anuluj
          </Button>
        </div>
      )}
    </div>
  );
}
